import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import AssistChip from 'LuxKit/Chips/AssistChip'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import TextLink from 'LuxKit/TextLink'
import { trackClientEvent } from 'analytics/analytics'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import ModalContext from 'contexts/ModalContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyTierStayUpgradeMessageBanner from 'loyalty/components/LoyaltyTierStayUpgradeMessageBanner'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'

import PropertyLoyaltyPackageUpgradeModal, { type PropertyLoyaltyPackageUpgradeModalResult } from './PropertyLoyaltyPackageUpgradeModal'
import usePropertyRateStayUpgrade from './usePropertyRateStayUpgrade'
import type { PropertyRateUpgradeCandidate } from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'

interface Props {
  candidate: PropertyRateUpgradeCandidate
  /** The property room's real id — needed to move the rate radio when Apply changes refundability. */
  roomId: string
  /**
   * The refundability option the customer is currently on, so Apply can honour it rather than always
   * dropping to the cheapest upgradeable one.
   */
  selectedRateId?: string
  propertyTimeZone: string
}

function PropertyOfferPageLoyaltyStayUpgradeCallOutBanner({ candidate, roomId, selectedRateId, propertyTimeZone }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  // Guaranteed by `<PropertyContainer/>`, which is the only thing that renders these rate tiles.
  const property = useContext(OfferPageStateContext).property!

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasUpgradeBenefit = useUserHasLoyaltyBenefit('hotel_upgrade')
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const isFeatureEnabled = useAppSelector(
    state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltySppPackageUpgrades],
  )
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const { appliedVariant, isApplied, applyTargetRate, applyUpgrade, removeUpgrade } = usePropertyRateStayUpgrade(candidate, roomId, selectedRateId)

  // Apply lands on the option the customer is already looking at, and only falls back to the cheapest
  // upgradeable one when theirs isn't offered an upgrade.
  const onToggleUpgrade = useCallback(() => {
    if (isApplied) {
      removeUpgrade()
    } else if (applyTargetRate) {
      applyUpgrade(applyTargetRate)
    }
  }, [isApplied, removeUpgrade, applyUpgrade, applyTargetRate])

  const onOpenModal = useCallback(async() => {
    trackClientEvent({
      subject: 'loyalty_package_upgrade',
      action: 'opened',
      category: 'offer_page',
      type: 'interaction',
    })

    const result = await showModal<PropertyLoyaltyPackageUpgradeModalResult | undefined>(
      <PropertyLoyaltyPackageUpgradeModal
        candidate={candidate}
        property={property}
        appliedSourceRateId={appliedVariant?.rate.rateId}
        propertyTimeZone={propertyTimeZone}
      />,
    )
    if (!result) return

    if (result.action === 'remove') {
      removeUpgrade()
    } else if (result.action === 'apply' && result.sourceRate) {
      applyUpgrade(result.sourceRate)
    }
  }, [showModal, candidate, property, appliedVariant, propertyTimeZone, applyUpgrade, removeUpgrade])

  if (!isLoyaltyEnabled || !hasUpgradeBenefit || !isCurrentRegionUserLoyaltyRegion || !isFeatureEnabled) {
    return null
  }

  // The tier is named with the program in front of it, matching the room list's upgrade filter chip.
  // Infinite is the exception: it carries its own branding and is never prefixed with the program
  // name, the same rule the points filter and the account hero follow.
  const tierName = getLoyaltyTierDisplayName(tier)
  const programName = getLoyaltyProgramDisplayName()
  const isInfiniteTier = tier === 'infinite'

  let availableMessage: string
  if (isInfiniteTier) {
    availableMessage = intl.formatMessage(
      {
        id: 'h1I508QpqT5UomVS1KDQ',
        defaultMessage: 'Free package upgrade available with {tierName}.',
        description: 'Call-out on the offer page room card telling a loyalty member a free package upgrade is available',
      },
      { tierName },
    )
  } else {
    availableMessage = intl.formatMessage(
      {
        id: 'pt-T9CV-BkZobA8-Slgi',
        defaultMessage: 'Free package upgrade available with {program} {tierName}.',
        description: 'Call-out on the offer page room card telling a loyalty member of a named program tier that a free package upgrade is available',
      },
      { program: programName, tierName },
    )
  }

  const appliedMessage = intl.formatMessage({
    id: '5ADIkI0I-vSwDo8018HH',
    defaultMessage: 'Your free upgrade has been applied!',
    description: 'Confirmation on the offer page room card that the loyalty free package upgrade is applied',
  })
  const detailsLabel = intl.formatMessage({
    id: 'KX4B1j_FX7527CJFmjJr',
    defaultMessage: 'Details',
    description: 'Link on the offer page room card that opens the loyalty free package upgrade details modal',
  })

  const message = isApplied ? appliedMessage : availableMessage
  // Celebratory leading icon only once the upgrade is on — the available state stays plain.
  const leadingIcon = isApplied ? <LineFestivalsIcon /> : false

  return <LoyaltyTierStayUpgradeMessageBanner
    tier={tier}
    icon={leadingIcon}
    endContent={<AssistChip
      variant="filled"
      size="small"
      onClick={onToggleUpgrade}
      // A cross only on the pill that closes the upgrade out; Apply adds one, so it stays bare.
      endIcon={isApplied ? <LineTimesIcon /> : undefined}
    >
      {isApplied
        ? intl.formatMessage({
            id: 'H7tt8lLY7gtfmauI0eqN',
            defaultMessage: 'Remove',
            description: 'Button on the offer page room card that removes the applied loyalty free package upgrade',
          })
        : intl.formatMessage({
            id: '52MnR5GMWaxXf5w0r-fr',
            defaultMessage: 'Apply',
            description: 'Button on the offer page room card that opens the loyalty free package upgrade modal',
          })}
    </AssistChip>}
  >
    {/* The message and its `Details` link sit in one wrapper so the banner's flex row treats them as a
        single item. Left as siblings they are two, and the narrowest cards — the mobile carousel's,
        which this bar now also sits in — push `Details` onto a line of its own. */}
    <span><b>{message}</b> <TextLink weight="regular" size="medium" underline onClick={onOpenModal}>{detailsLabel}</TextLink></span>
  </LoyaltyTierStayUpgradeMessageBanner>
}

export default PropertyOfferPageLoyaltyStayUpgradeCallOutBanner
